/**
 * Project Details View (Hub)
 * Central view for a project with edit, staging, and overlay access
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Wand2,
  Layers,
  Image as ImageIcon,
  Loader2,
  ArrowLeft,
  Maximize2,
  CheckSquare,
  Square,
  Trash2,
  X,
  UserPlus,
  UserMinus,
  Crown,
  Download,
  Eye,
  Upload,
  LayoutGrid,
  Box,
  RefreshCw,
  ChevronDown,
} from 'lucide-react';
import { saveAs } from 'file-saver';
import { Project, GalleryImage, ProjectMember, HeroOverlayConfig, FloorplanVersion, RoomType, BoxCoordinates } from '@/types';
import { applyAiLabel, buildExportZip, exportZipFileName, galleryEntriesForVersions, type FloorplanEntry } from '@/services/exportService';
import { getAvatarUrl } from '@/services/avatarService';
import {
  updateProject,
  fetchProjectGalleryImages,
  deleteImageVersion,
  deleteMultipleImages,
  uploadProjectImage,
} from '@/services/projectService';
import { fetchFloorplanImage, generateFloorplan, fetchVersionsWithMessages } from '@/services/floorplanService';
import { getRoomLabel } from '@/lib/roomConfig';
import { generateRoomImage, requestStageRefinement, requestStageRefinementPreview } from '@/services/geminiService';
import { RoomDetailModal, HeroOverlayEditor, RegenerationModal } from '@/components/ui';
import { AiLabelOption, useAiLabelPrefs } from '@/components/ui/AiLabelOption';
import { supabase, getImageUrl } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useAppSettings } from '@/hooks/useAppSettings';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

interface ProjectDetailsViewProps {
  project: Project;
  onBack: () => void;
  onOpenStaging: () => void;
  onOpenStagingWithSelection: (selectedIds: Set<string>) => void;
  onOpenOverlay: () => void;
  onOpenFloorplan: () => void;
  onImageClick: (url: string) => void;
  onProjectUpdate: (updates: Partial<Project>) => void;
  onHeroSave: (imageId: string, heroDataUrl: string, config: HeroOverlayConfig) => Promise<string>;
  isAdmin?: boolean;
  teamMembers?: Array<{ id: string; name: string; email: string; avatarPath: string | null }>;
}

export const ProjectDetailsView: React.FC<ProjectDetailsViewProps> = ({
  project,
  onBack,
  onOpenStaging,
  onOpenStagingWithSelection,
  onOpenOverlay,
  onOpenFloorplan,
  onImageClick,
  onProjectUpdate,
  onHeroSave,
  isAdmin,
  teamMembers,
}) => {
  const { user, disabledFeatures } = useAuth();
  const { settings: appSettings } = useAppSettings();
  const floorplanEnabled = isAdmin || !disabledFeatures.includes('floorplan');

  // Editable fields
  const [name, setName] = useState(project.name);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdatingUploadSettings, setIsUpdatingUploadSettings] = useState(false);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle');
  const [uploadToken, setUploadToken] = useState(project.uploadToken ?? '');
  const [isUploadActive, setIsUploadActive] = useState(project.isUploadActive ?? true);

  // Gallery
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [isLoadingImages, setIsLoadingImages] = useState(true);

  // Floorplan previews + metadata
  const [floorplan2dUrl, setFloorplan2dUrl] = useState<string | null>(null);
  const [floorplan3dUrl, setFloorplan3dUrl] = useState<string | null>(null);
  const [floorplanDbId, setFloorplanDbId] = useState<string | null>(null);
  const [floorplan2dPath, setFloorplan2dPath] = useState<string | null>(null);
  const [is3dGenerating, setIs3dGenerating] = useState(false);
  const [generate3dError, setGenerate3dError] = useState<string | null>(null);
  const [floorplanCollapsed, setFloorplanCollapsed] = useState(false);
  // Branded floorplan URLs (composite after "Speichern & Fertig" in Branding view)
  const [floorplan2dBrandedUrl, setFloorplan2dBrandedUrl] = useState<string | null>(null);
  const [floorplan3dBrandedUrl, setFloorplan3dBrandedUrl] = useState<string | null>(null);
  // Version history for the "Komplett neu generieren" strip
  const [floorplanVersions, setFloorplanVersions] = useState<FloorplanVersion[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [tenantSectionCollapsed, setTenantSectionCollapsed] = useState(true);

  // Room Detail Modal
  const [selectedRoom, setSelectedRoom] = useState<GalleryImage | null>(null);

  // Regeneration / Edit Modal
  const [activeRegen, setActiveRegen] = useState<{
    imageId: string;
    version: 'empty' | 'staged';
    currentStoragePath?: string;
    currentUrl?: string;
    initialMode?: 'choose' | 'feedback';
  } | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenError, setRegenError] = useState<string | null>(null);
  const [regenSuggestion, setRegenSuggestion] = useState<{ reason: string | null } | null>(null);

  // Selection Mode
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  // Hero Overlay Editor
  const [heroEditorImage, setHeroEditorImage] = useState<GalleryImage | null>(null);

  // Direct upload from project overview
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Project Members
  const [projectMembers, setProjectMembers] = useState<ProjectMember[]>([]);
  const [, setIsLoadingMembers] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);

  // Reset fields when project changes
  useEffect(() => {
    setName(project.name);
    setUploadToken(project.uploadToken ?? '');
    setIsUploadActive(project.isUploadActive ?? true);
  }, [project]);

  useEffect(() => {
    if (copyStatus === 'idle') return;
    const timeoutId = window.setTimeout(() => setCopyStatus('idle'), 2000);
    return () => window.clearTimeout(timeoutId);
  }, [copyStatus]);

  // Load gallery images
  const loadImages = useCallback(async () => {
    setIsLoadingImages(true);
    try {
      const data = await fetchProjectGalleryImages(project.id);
      setImages(data);
    } catch (error) {
      console.error('Fehler beim Laden der Bilder:', error);
    } finally {
      setIsLoadingImages(false);
    }
  }, [project.id]);

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  // Load floorplan previews + metadata (including branded composites + version history)
  useEffect(() => {
    const loadFloorplan = async () => {
      try {
        const fp = await fetchFloorplanImage(project.id);
        if (!fp) {
          setFloorplan2dUrl(null);
          setFloorplan3dUrl(null);
          setFloorplanDbId(null);
          setFloorplan2dPath(null);
          setFloorplan2dBrandedUrl(null);
          setFloorplan3dBrandedUrl(null);
          setFloorplanVersions([]);
          setSelectedVersionId(null);
          return;
        }
        setFloorplanDbId(fp.id);
        setFloorplan2dPath(fp.storage_path_floorplan_2d || null);
        setFloorplan2dUrl(fp.storage_path_floorplan_2d ? getImageUrl(fp.storage_path_floorplan_2d) : null);
        setFloorplan3dUrl(fp.storage_path_floorplan_3d ? getImageUrl(fp.storage_path_floorplan_3d) : null);
        // Branded composites (set by "Speichern & Fertig" in FloorplanBrandingView)
        setFloorplan2dBrandedUrl(fp.storage_path_floorplan_2d_branded ? getImageUrl(fp.storage_path_floorplan_2d_branded) : null);
        setFloorplan3dBrandedUrl(fp.storage_path_floorplan_3d_branded ? getImageUrl(fp.storage_path_floorplan_3d_branded) : null);
        // Version history (each "Komplett neu generieren" creates a new version)
        try {
          const { versions } = await fetchVersionsWithMessages(fp.id);
          setFloorplanVersions(versions);
          setSelectedVersionId(fp.active_version_id ?? (versions[versions.length - 1]?.id ?? null));
        } catch (err) {
          console.warn('[ProjectDetails] Failed to load floorplan versions:', err);
          setFloorplanVersions([]);
          setSelectedVersionId(null);
        }
      } catch {
        console.error('[ProjectDetails] Failed to load floorplan');
      }
    };
    loadFloorplan();
  }, [project.id]);

  // =========================================================================
  // Realtime Subscription for Version History Updates
  // =========================================================================
  useEffect(() => {
    // Subscribe to changes on the images table for this project
    const imagesChannel = supabase
      .channel(`images-${project.id}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen for INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'images',
          filter: `project_id=eq.${project.id}`,
        },
        (payload) => {
          console.log('[Realtime] Images table change:', payload.eventType);
          // Reload images when there's a change
          loadImages();
        }
      )
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(imagesChannel);
    };
  }, [project.id, loadImages]);

  // Exit selection mode when no images selected
  useEffect(() => {
    if (isSelectionMode && selectedImages.size === 0 && images.length > 0) {
      // Keep selection mode active even with 0 selected
    }
  }, [selectedImages, isSelectionMode, images.length]);

  // Keep selectedRoom in sync with images (for realtime updates)
  useEffect(() => {
    if (selectedRoom) {
      const updatedImage = images.find(img => img.id === selectedRoom.id);
      if (updatedImage) {
        // Only update if something actually changed
        const hasChanged = 
          updatedImage.emptyUrl !== selectedRoom.emptyUrl ||
          updatedImage.stagedUrl !== selectedRoom.stagedUrl ||
          updatedImage.brandedUrl !== selectedRoom.brandedUrl ||
          updatedImage.brandedEmptyUrl !== selectedRoom.brandedEmptyUrl;
        
        if (hasChanged) {
          console.log('[Sync] Updating selectedRoom with new image data');
          setSelectedRoom(updatedImage);
        }
      }
    }
  }, [images, selectedRoom]);

  // Save on blur
  const handleNameBlur = useCallback(async () => {
    if (name === project.name) return;
    if (!name.trim()) {
      setName(project.name);
      return;
    }

    setIsSaving(true);
    try {
      await updateProject(project.id, { name: name.trim() });
      onProjectUpdate({ name: name.trim() });
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      setName(project.name);
    } finally {
      setIsSaving(false);
    }
  }, [name, project, onProjectUpdate]);

  // Save property detail on blur
  // Hero save handler
  const handleHeroSaveInternal = useCallback(async (imageId: string, heroDataUrl: string, config: HeroOverlayConfig) => {
    const heroUrl = await onHeroSave(imageId, heroDataUrl, config);
    // Update local gallery state with config + URL
    setImages(prev => prev.map(img =>
      img.id === imageId ? { ...img, heroOverlayConfig: config, heroUrl: heroUrl || img.heroUrl } : img
    ));
  }, [onHeroSave]);

  // Handle 3D floorplan generation directly from project view
  const handleGenerate3D = useCallback(async () => {
    if (!floorplan2dPath || !floorplanDbId || !user) return;
    setIs3dGenerating(true);
    setGenerate3dError(null);
    try {
      const result = await generateFloorplan(
        '3d_generation',
        floorplan2dPath,
        project.id,
        floorplanDbId,
        user.id,
        undefined,
        undefined
      );
      if (result.imageUrl) {
        setFloorplan3dUrl(result.imageUrl);
      }
    } catch (err) {
      console.error('[ProjectDetails] 3D generation failed:', err);
      setGenerate3dError(err instanceof Error ? err.message : '3D-Generierung fehlgeschlagen');
    } finally {
      setIs3dGenerating(false);
    }
  }, [floorplan2dPath, floorplanDbId, project.id, user]);

  // Handle delete version
  const handleDeleteVersion = useCallback(async (
    imageId: string,
    version: 'original' | 'empty' | 'staged' | 'upscaled_staged' | 'upscaled_empty' | 'branded' | 'branded_empty',
    storagePath: string
  ) => {
    if (version === 'original') return; // Can't delete original

    await deleteImageVersion(imageId, version as 'empty' | 'staged' | 'upscaled_staged' | 'upscaled_empty' | 'branded' | 'branded_empty', storagePath);

    // Update local state
    setImages(prev => prev.map(img => {
      if (img.id === imageId) {
        const updated = { ...img };
        switch (version) {
          case 'empty': updated.emptyUrl = undefined; break;
          case 'staged': updated.stagedUrl = undefined; break;
          case 'upscaled_staged': updated.upscaledStagedUrl = undefined; break;
          case 'upscaled_empty': updated.upscaledEmptyUrl = undefined; break;
          case 'branded': updated.brandedUrl = undefined; break;
          case 'branded_empty': updated.brandedEmptyUrl = undefined; break;
        }
        return updated;
      }
      return img;
    }));

    // Update selected room if open
    if (selectedRoom?.id === imageId) {
      setSelectedRoom(prev => {
        if (!prev) return null;
        const updated = { ...prev };
        switch (version) {
          case 'empty': updated.emptyUrl = undefined; break;
          case 'staged': updated.stagedUrl = undefined; break;
          case 'upscaled_staged': updated.upscaledStagedUrl = undefined; break;
          case 'upscaled_empty': updated.upscaledEmptyUrl = undefined; break;
          case 'branded': updated.brandedUrl = undefined; break;
          case 'branded_empty': updated.brandedEmptyUrl = undefined; break;
        }
        return updated;
      });
    }
  }, [selectedRoom]);

  // Direct re-roll: regenerate the current empty or staged version without any feedback
  const handleRegenerate = useCallback(async (version: 'empty' | 'staged') => {
    if (!selectedRoom) return;
    const image = images.find(img => img.id === selectedRoom.id);
    if (!image?.originalUrl) return;

    const match = image.originalUrl.match(/\/images\/(.+)$/);
    const originalPath = match ? match[1] : '';
    if (!originalPath) return;

    setIsRegenerating(true);
    const versionType = version === 'empty' ? 'Empty' : 'Staged';
    try {
      const result = await generateRoomImage(
        originalPath,
        project.id,
        image.roomType,
        versionType as any,
        false,
        undefined,
        image.customLabel,
        image.id,
        undefined,
        user?.id
      );

      if (result.success && result.imageUrl) {
        const urlField = version === 'empty' ? 'emptyUrl' : 'stagedUrl';
        const storageField = version === 'empty' ? 'emptyStoragePath' : 'stagedStoragePath';
        setImages(prev => prev.map(img =>
          img.id === image.id ? { ...img, [urlField]: result.imageUrl, [storageField]: result.storagePath } : img
        ));
      }
    } catch (err) {
      console.error('[ProjectDetails] Regenerate failed:', err);
    } finally {
      setIsRegenerating(false);
    }
  }, [selectedRoom, images, project.id, user]);

  // Modal confirmation: feedback-based refinement or re-roll with instructions
  const handleRegenConfirm = useCallback(async (
    feedback: string | null,
    isRefinement: boolean,
    boxCoords?: BoxCoordinates,
    override?: { finalPrompt: string; scope: 'global' | 'local'; intent: 'cosmetic' | 'structural' },
    forceEdit?: boolean
  ) => {
    if (!activeRegen) return;
    const image = images.find(img => img.id === activeRegen.imageId);
    if (!image?.originalUrl) return;

    const { version, currentStoragePath } = activeRegen;
    const versionType = version === 'empty' ? 'Empty' : 'Staged';
    const urlField = version === 'empty' ? 'emptyUrl' : 'stagedUrl';
    const storageField = version === 'empty' ? 'emptyStoragePath' : 'stagedStoragePath';

    const useRefinementPath =
      isRefinement &&
      !!feedback &&
      !!currentStoragePath &&
      (isAdmin ? appSettings.stage_refinement_admin : appSettings.stage_refinement_live);

    setIsRegenerating(true);
    setRegenError(null);
    setRegenSuggestion(null);
    try {
      if (useRefinementPath && feedback && currentStoragePath) {
        const result = await requestStageRefinement({
          storagePath: currentStoragePath,
          imageDbId: activeRegen.imageId,
          versionType,
          userPrompt: feedback,
          boxCoords,
          promptOverride: override?.finalPrompt,
          scopeOverride: override?.scope,
          intentOverride: override?.intent,
          forceEdit,
        });
        // Advisory (perspective etc.): no image generated, no credit — show it.
        if (result.regenerate_suggested) {
          setRegenSuggestion({ reason: result.regenerate_reason ?? null });
          return;
        }
        const newPublicUrl = supabase.storage.from('images').getPublicUrl(result.storage_path!).data.publicUrl;

        setImages(prev => prev.map(img =>
          img.id === activeRegen.imageId ? { ...img, [urlField]: newPublicUrl, [storageField]: result.storage_path } : img
        ));

        const dbField = version === 'empty' ? 'storage_path_empty' : 'storage_path_staged';
        supabase
          .from('images')
          .update({ [dbField]: result.storage_path })
          .eq('id', activeRegen.imageId)
          .then(() => {}, (err) => console.warn('[ProjectDetails] images table persist failed', err));
      } else {
        const match = image.originalUrl?.match(/\/images\/(.+)$/);
        const originalPath = match ? match[1] : '';
        if (!originalPath) throw new Error('Storage path not found');

        const sourceImagePath = isRefinement && currentStoragePath ? currentStoragePath : undefined;
        const isStagedRegen = version === 'staged';
        const result = await generateRoomImage(
          originalPath,
          project.id,
          image.roomType,
          versionType as any,
          false,
          feedback ?? undefined,
          image.customLabel,
          image.id,
          sourceImagePath,
          user?.id,
          project.aspectRatio,
          isStagedRegen ? project.stagingStyle : undefined,
          undefined, // adminTest
          1, // variantCount: single re-roll, not batch generation
          isStagedRegen && (isAdmin ? appSettings.catalog_staging_admin : appSettings.catalog_staging_live) ? image.emptyStoragePath : undefined,
        );

        if (result.success && result.imageUrl) {
          setImages(prev => prev.map(img =>
            img.id === activeRegen.imageId ? { ...img, [urlField]: result.imageUrl, [storageField]: result.storagePath } : img
          ));
        }
      }
      // Close only on success so the user never loses their input on error
      setActiveRegen(null);
      setRegenError(null);
    } catch (err) {
      console.error('[ProjectDetails] Regen confirm failed:', err);
      setRegenError((err as Error).message || 'Regenerierung fehlgeschlagen.');
    } finally {
      setIsRegenerating(false);
    }
  }, [activeRegen, images, project.id, isAdmin, appSettings.stage_refinement_admin, appSettings.stage_refinement_live, appSettings.catalog_staging_admin, appSettings.catalog_staging_live, user]);

  // Handle restore version from history
  const handleRestoreVersion = useCallback(async (
    imageId: string,
    versionType: 'Empty' | 'Staged' | 'Floorplan2D' | 'Floorplan3D',
    storagePath: string
  ) => {
    // Determine the field to update based on version type
    const updateField = versionType === 'Empty' 
      ? 'storage_path_empty' 
      : 'storage_path_staged';

    // Update the database
    const { error } = await supabase
      .from('images')
      .update({ [updateField]: storagePath })
      .eq('id', imageId);

    if (error) {
      console.error('Restore failed:', error);
      throw error;
    }

    // Get the public URL for the restored image
    const { data: urlData } = supabase.storage
      .from('images')
      .getPublicUrl(storagePath);

    const newUrl = urlData.publicUrl;

    // Update local state
    setImages(prev => prev.map(img => {
      if (img.id === imageId) {
        return versionType === 'Empty'
          ? { ...img, emptyUrl: newUrl }
          : { ...img, stagedUrl: newUrl };
      }
      return img;
    }));

    // Update selected room if open
    if (selectedRoom?.id === imageId) {
      setSelectedRoom(prev => {
        if (!prev) return null;
        return versionType === 'Empty'
          ? { ...prev, emptyUrl: newUrl }
          : { ...prev, stagedUrl: newUrl };
      });
    }
  }, [selectedRoom]);

  // Selection handlers
  const toggleSelectionMode = useCallback(() => {
    setIsSelectionMode(prev => !prev);
    setSelectedImages(new Set());
  }, []);

  const toggleImageSelection = useCallback((imageId: string) => {
    setSelectedImages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(imageId)) {
        newSet.delete(imageId);
      } else {
        newSet.add(imageId);
      }
      return newSet;
    });
  }, []);

  const selectAllImages = useCallback(() => {
    setSelectedImages(new Set(images.map(img => img.id)));
  }, [images]);

  const deselectAllImages = useCallback(() => {
    setSelectedImages(new Set());
  }, []);

  // Delete selected images
  const handleDeleteSelected = useCallback(async () => {
    if (selectedImages.size === 0) return;

    const confirmDelete = window.confirm(
      `Möchtest du ${selectedImages.size} Bild${selectedImages.size > 1 ? 'er' : ''} wirklich löschen? Dies kann nicht rückgängig gemacht werden.`
    );

    if (!confirmDelete) return;

    setIsDeleting(true);
    try {
      await deleteMultipleImages(Array.from(selectedImages));
      
      // Update local state
      setImages(prev => prev.filter(img => !selectedImages.has(img.id)));
      setSelectedImages(new Set());
      setIsSelectionMode(false);
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      alert('Fehler beim Löschen der Bilder');
    } finally {
      setIsDeleting(false);
    }
  }, [selectedImages]);

  const handleDirectUpload = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const fileArray = Array.from(files);
    setIsUploadingFiles(true);
    setUploadProgress({ done: 0, total: fileArray.length });
    let done = 0;
    for (const file of fileArray) {
      try {
        await uploadProjectImage(project.id, file, RoomType.CUSTOM, false);
      } catch (error) {
        console.error('[Upload] Failed:', error);
      }
      done++;
      setUploadProgress({ done, total: fileArray.length });
    }
    setIsUploadingFiles(false);
    setUploadProgress(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    await loadImages();
  }, [project.id, loadImages]);

  // Download modal state
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadVersions, setDownloadVersions] = useState({
    branded: true,
    staged: true,
    empty: false,
    original: false,
    hero: true,
    floorplan: true,
  });
  const [downloadNotice, setDownloadNotice] = useState<string | null>(null);
  const aiLabel = useAiLabelPrefs();

  // Derived: the version the user has clicked in the history strip (null = show current/branded)
  const selectedVersion = useMemo(
    () => floorplanVersions.find((v) => v.id === selectedVersionId) ?? null,
    [floorplanVersions, selectedVersionId]
  );

  // Derived 2D "clean" URL (without branding): user-selected version > raw active variant
  const cleanFloorplan2dUrl = useMemo(() => {
    if (selectedVersion?.initialStoragePath) return getImageUrl(selectedVersion.initialStoragePath);
    return floorplan2dUrl;
  }, [selectedVersion, floorplan2dUrl]);

  // Derived 3D "clean" URL (without branding)
  const cleanFloorplan3dUrl = floorplan3dUrl;

  // Derived 3D display URL: branded composite > raw (no per-version 3D)
  // — used as fallback for the single-tile rendering path when no branded composite exists.
  const displayedFloorplan3dUrl = useMemo(
    () => floorplan3dBrandedUrl ?? floorplan3dUrl,
    [floorplan3dBrandedUrl, floorplan3dUrl]
  );

  const toggleDownloadVersion = (key: string) => {
    setDownloadVersions(prev => ({ ...prev, [key]: !prev[key as keyof typeof prev] }));
  };

  // ZIP download — unified structure: Immobilienbilder/ + optional Grundriss/
  const handleZipDownload = useCallback(async () => {
    const imagesToDownload = isSelectionMode
      ? images.filter(img => selectedImages.has(img.id))
      : images;

    const floorplans: FloorplanEntry[] = [];
    if (downloadVersions.floorplan) {
      const url2d = floorplan2dBrandedUrl ?? floorplan2dUrl;
      const url3d = floorplan3dBrandedUrl ?? floorplan3dUrl;
      if (url2d) floorplans.push({ side: '2d', source: url2d });
      if (url3d) floorplans.push({ side: '3d', source: url3d });
    }

    const entries = galleryEntriesForVersions(imagesToDownload, downloadVersions);
    if (entries.length === 0 && floorplans.length === 0) return;

    setIsDownloading(true);
    setDownloadProgress(0);
    setDownloadNotice(null);
    try {
      // KI-Hinweis als letzter Schritt vor dem Packen — Originale bleiben unberührt.
      const labeled = aiLabel.enabled
        ? await applyAiLabel(entries, floorplans, aiLabel.position, aiLabel.opacity)
        : { entries, floorplans };
      const result = await buildExportZip(labeled.entries, labeled.floorplans, setDownloadProgress);
      saveAs(result.blob, exportZipFileName(project.name));
      if (result.failed > 0) {
        setDownloadNotice(`${result.added} von ${result.total} Dateien exportiert — einige Bilder konnten nicht geladen werden.`);
      } else {
        setShowDownloadModal(false);
      }
    } catch (error) {
      console.error('[Export] ZIP fehlgeschlagen:', error);
      setDownloadNotice('Der Export ist fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  }, [
    images, isSelectionMode, selectedImages, downloadVersions, project.name,
    floorplan2dUrl, floorplan3dUrl, floorplan2dBrandedUrl, floorplan3dBrandedUrl,
    aiLabel.enabled, aiLabel.position, aiLabel.opacity,
  ]);

  // Load project members (for admins with team)
  const loadProjectMembers = useCallback(async () => {
    if (!isAdmin || !teamMembers?.length) return;
    setIsLoadingMembers(true);
    try {
      const { data, error } = await supabase.rpc('get_project_members', { p_project_id: project.id });
      if (error) throw error;
      const members: ProjectMember[] = (data || []).map((m: { member_id: string; member_email: string; member_name: string | null; avatar_path: string | null; assigned_at: string }) => ({
        memberId: m.member_id,
        memberEmail: m.member_email,
        memberName: m.member_name,
        avatarPath: m.avatar_path,
        assignedAt: m.assigned_at,
      }));
      setProjectMembers(members);
    } catch (error) {
      console.error('[ProjectMembers] Failed to load:', error);
    } finally {
      setIsLoadingMembers(false);
    }
  }, [isAdmin, teamMembers, project.id]);

  useEffect(() => {
    loadProjectMembers();
  }, [loadProjectMembers]);

  const handleAssignMember = useCallback(async (memberId: string) => {
    setIsAssigning(true);
    try {
      const { error } = await supabase.rpc('assign_project_member', {
        p_project_id: project.id,
        p_member_id: memberId,
      });
      if (error) throw error;
      await loadProjectMembers();
    } catch (error) {
      console.error('[ProjectMembers] Assign failed:', error);
    } finally {
      setIsAssigning(false);
    }
  }, [project.id, loadProjectMembers]);

  const handleUnassignMember = useCallback(async (memberId: string) => {
    try {
      const { error } = await supabase.rpc('unassign_project_member', {
        p_project_id: project.id,
        p_member_id: memberId,
      });
      if (error) throw error;
      setProjectMembers(prev => prev.filter(m => m.memberId !== memberId));
    } catch (error) {
      console.error('[ProjectMembers] Unassign failed:', error);
    }
  }, [project.id]);

  // Members not yet assigned to this project
  const assignableMembers = (teamMembers || []).filter(
    tm => !projectMembers.some(pm => pm.memberId === tm.id)
  );

  // Count actual overlay tool images (each variant = 1 image) + floorplans
  const overlayImageCount = images.reduce((count, img) => {
    if (img.brandedUrl || img.stagedUrl) count++;
    if (img.brandedEmptyUrl || img.emptyUrl) count++;
    return count;
  }, 0) + (floorplan2dUrl ? 1 : 0) + (floorplan3dUrl ? 1 : 0);
  const tenantUploadCount = images.filter(img => img.isTenantUpload).length;
  const tenantUploadLink = typeof window !== 'undefined' && uploadToken
    ? `${window.location.origin}/upload/${uploadToken}`
    : '';

  const handleCopyTenantLink = useCallback(async () => {
    if (!tenantUploadLink) return;
    try {
      await navigator.clipboard.writeText(tenantUploadLink);
      setCopyStatus('copied');
    } catch (error) {
      console.error('Copy failed:', error);
      setCopyStatus('error');
    }
  }, [tenantUploadLink]);

  const handleRegenerateToken = useCallback(async () => {
    if (isUpdatingUploadSettings) return;
    const newToken = typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    setIsUpdatingUploadSettings(true);
    try {
      await updateProject(project.id, { upload_token: newToken });
      setUploadToken(newToken);
      onProjectUpdate({ uploadToken: newToken });
    } catch (error) {
      console.error('Token konnte nicht erneuert werden:', error);
      alert('Token konnte nicht erneuert werden');
    } finally {
      setIsUpdatingUploadSettings(false);
    }
  }, [isUpdatingUploadSettings, onProjectUpdate, project.id]);

  const handleToggleUploadActive = useCallback(async (nextValue: boolean) => {
    if (isUpdatingUploadSettings) return;
    setIsUpdatingUploadSettings(true);
    try {
      await updateProject(project.id, { is_upload_active: nextValue });
      setIsUploadActive(nextValue);
      onProjectUpdate({ isUploadActive: nextValue });
    } catch (error) {
      console.error('Upload Status konnte nicht aktualisiert werden:', error);
      alert('Upload Status konnte nicht aktualisiert werden');
    } finally {
      setIsUpdatingUploadSettings(false);
    }
  }, [isUpdatingUploadSettings, onProjectUpdate, project.id]);

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Hidden file input for direct upload */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,.heic,.heif"
        className="hidden"
        onChange={(e) => handleDirectUpload(e.target.files)}
      />

      {/* Room Detail Modal */}
      {selectedRoom && (
        <RoomDetailModal
          image={selectedRoom}
          projectId={project.id}
          onClose={() => setSelectedRoom(null)}
          onDeleteVersion={handleDeleteVersion}
          onRegenerate={handleRegenerate}
          onEdit={(version, storagePath, currentUrl) => {
            setActiveRegen({ imageId: selectedRoom.id, version, currentStoragePath: storagePath, currentUrl, initialMode: 'feedback' });
          }}
          isRegenerating={isRegenerating}
          onRestoreVersion={handleRestoreVersion}
        />
      )}

      {/* Regeneration / Edit Modal */}
      {activeRegen && (
        <RegenerationModal
          onClose={() => { if (!isRegenerating) { setActiveRegen(null); setRegenError(null); setRegenSuggestion(null); } }}
          onConfirm={handleRegenConfirm}
          onAnalyze={async (feedbackText, boxCoords, opts) => {
            if (!activeRegen.currentStoragePath) {
              throw new Error('Analyse nicht möglich — kein aktuelles Bild.');
            }
            return requestStageRefinementPreview({
              storagePath: activeRegen.currentStoragePath,
              imageDbId: activeRegen.imageId,
              versionType: activeRegen.version === 'empty' ? 'Empty' : 'Staged',
              userPrompt: feedbackText,
              boxCoords,
              classifierPromptOverride: opts?.classifierPromptOverride,
              rewritePromptOverride: opts?.rewritePromptOverride,
              rewritePromptOverrideKey: opts?.rewritePromptOverrideKey,
              assemblyTemplateOverride: opts?.assemblyTemplateOverride,
              assemblyTemplateOverrideKey: opts?.assemblyTemplateOverrideKey,
            });
          }}
          isLoading={isRegenerating}
          error={regenError}
          regenerateSuggestion={regenSuggestion}
          hasCurrentImage={!!activeRegen.currentStoragePath}
          currentImageUrl={activeRegen.currentUrl}
          initialMode={activeRegen.initialMode ?? 'choose'}
        />
      )}

      {/* Hero Overlay Editor Modal */}
      {heroEditorImage && (
        <HeroOverlayEditor
          image={heroEditorImage}
          project={project}
          onClose={() => setHeroEditorImage(null)}
          onSave={handleHeroSaveInternal}
        />
      )}

      {/* Download Modal */}
      <Dialog open={showDownloadModal} onOpenChange={setShowDownloadModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bilder herunterladen</DialogTitle>
            <DialogDescription>
              {isSelectionMode ? `${selectedImages.size} ausgewählte Bilder` : `${images.length} Bilder`} — Wähle die Versionen aus
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {(() => {
              const imagesToCheck = isSelectionMode
                ? images.filter(img => selectedImages.has(img.id))
                : images;

              const versionOptions = [
                {
                  key: 'branded',
                  label: 'Gebrandet',
                  count: imagesToCheck.filter(img => img.brandedUrl || img.brandedEmptyUrl).length,
                },
                {
                  key: 'staged',
                  label: 'Eingerichtet',
                  count: imagesToCheck.filter(img => img.stagedUrl).length,
                },
                {
                  key: 'empty',
                  label: 'Leer',
                  count: imagesToCheck.filter(img => img.emptyUrl).length,
                },
                {
                  key: 'original',
                  label: 'Original',
                  count: imagesToCheck.filter(img => img.originalUrl).length,
                },
                {
                  key: 'hero',
                  label: 'Titelbild',
                  count: imagesToCheck.filter(img => img.heroUrl).length,
                },
                {
                  key: 'floorplan',
                  label: 'Grundriss',
                  count: (floorplan2dUrl ? 1 : 0) + (floorplan3dUrl ? 1 : 0),
                },
              ];

              return versionOptions
                .filter(opt => opt.count > 0)
                .map(opt => (
                  <label
                    key={opt.key}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={downloadVersions[opt.key as keyof typeof downloadVersions]}
                      onChange={() => toggleDownloadVersion(opt.key)}
                      className="h-4 w-4 rounded border-input"
                    />
                    <span className="flex-1 text-sm font-medium">{opt.label}</span>
                    <span className="text-xs text-muted-foreground">{opt.count} Dateien</span>
                  </label>
                ));
            })()}
          </div>

          {/* Letzte Export-Option direkt über den Aktionen */}
          <AiLabelOption {...aiLabel} />

          {isDownloading && (
            <div className="space-y-2">
              <Progress value={downloadProgress} className="h-2" />
              <p className="text-xs text-muted-foreground text-center">{downloadProgress}% — Bilder werden gepackt...</p>
            </div>
          )}

          {downloadNotice && (
            <p className="text-xs text-amber-600">{downloadNotice}</p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDownloadModal(false)} disabled={isDownloading}>
              Abbrechen
            </Button>
            <Button
              onClick={handleZipDownload}
              disabled={isDownloading || !Object.values(downloadVersions).some(Boolean)}
              className="gap-2"
            >
              {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Als ZIP herunterladen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={onBack}
        className="flex items-center gap-2 pl-0 hover:pl-2 transition-all"
      >
        <ArrowLeft className="w-4 h-4" />
        Zurück zum Dashboard
      </Button>

      {/* Header + Property Details (merged) */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex-1 space-y-3">
              {/* Project Name (Editable) */}
              <div className="relative">
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={handleNameBlur}
                  className="text-2xl md:text-3xl font-bold text-foreground border-none shadow-none px-0 h-auto focus-visible:ring-0 focus:underline bg-transparent"
                  placeholder="Projektname"
                />
                {isSaving && (
                  <Loader2 className="absolute right-0 top-1/2 -translate-y-1/2 w-5 h-5 text-primary animate-spin" />
                )}
              </div>

            </div>

            {/* Stats + Team Avatars */}
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              {/* Team Members (Admin only) — single combined dropdown */}
              {isAdmin && teamMembers && teamMembers.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex items-center gap-1 cursor-pointer rounded-full hover:opacity-80 transition-opacity"
                      disabled={isAssigning}
                    >
                      {projectMembers.length > 0 ? (
                        <div className="flex -space-x-2">
                          {projectMembers.slice(0, 4).map((member) => (
                            member.avatarPath ? (
                              <img
                                key={member.memberId}
                                src={getAvatarUrl(member.avatarPath)}
                                alt={member.memberName || member.memberEmail}
                                className="w-7 h-7 rounded-full object-cover ring-2 ring-background"
                              />
                            ) : (
                              <div
                                key={member.memberId}
                                className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center ring-2 ring-background"
                              >
                                <span className="text-[10px] font-medium text-secondary-foreground">
                                  {(member.memberName || member.memberEmail).slice(0, 2).toUpperCase()}
                                </span>
                              </div>
                            )
                          ))}
                          {projectMembers.length > 4 && (
                            <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center ring-2 ring-background">
                              <span className="text-[10px] font-medium">+{projectMembers.length - 4}</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="w-7 h-7 rounded-full border-2 border-dashed border-muted-foreground/30 flex items-center justify-center">
                          <UserPlus className="w-3.5 h-3.5 text-muted-foreground" />
                        </div>
                      )}
                      {isAssigning && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-60">
                    {/* Assigned members section */}
                    {projectMembers.length > 0 && (
                      <>
                        <DropdownMenuLabel>Zugewiesen</DropdownMenuLabel>
                        {projectMembers.map((member) => (
                          <DropdownMenuItem
                            key={member.memberId}
                            onClick={() => handleUnassignMember(member.memberId)}
                          >
                            <div className="flex items-center gap-2 w-full">
                              {member.avatarPath ? (
                                <img src={getAvatarUrl(member.avatarPath)} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" />
                              ) : (
                                <div className="w-5 h-5 rounded-full bg-secondary flex items-center justify-center shrink-0">
                                  <span className="text-[10px] font-medium text-secondary-foreground">
                                    {(member.memberName || member.memberEmail).slice(0, 2).toUpperCase()}
                                  </span>
                                </div>
                              )}
                              <span className="truncate flex-1">{member.memberName || member.memberEmail}</span>
                              <UserMinus className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                            </div>
                          </DropdownMenuItem>
                        ))}
                      </>
                    )}
                    {/* Available members section */}
                    {assignableMembers.length > 0 && (
                      <>
                        {projectMembers.length > 0 && <DropdownMenuSeparator />}
                        <DropdownMenuLabel>Hinzufügen</DropdownMenuLabel>
                        {assignableMembers.map((member) => (
                          <DropdownMenuItem
                            key={member.id}
                            onClick={() => handleAssignMember(member.id)}
                          >
                            <div className="flex items-center gap-2 w-full">
                              {member.avatarPath ? (
                                <img src={getAvatarUrl(member.avatarPath)} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" />
                              ) : (
                                <div className="w-5 h-5 rounded-full bg-secondary flex items-center justify-center shrink-0">
                                  <span className="text-[10px] font-medium text-secondary-foreground">
                                    {(member.name || member.email).slice(0, 2).toUpperCase()}
                                  </span>
                                </div>
                              )}
                              <span className="truncate flex-1">{member.name || member.email}</span>
                              <UserPlus className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                            </div>
                          </DropdownMenuItem>
                        ))}
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <div className="flex items-center gap-1.5">
                <ImageIcon className="w-4 h-4" />
                <span>{images.length} Bilder</span>
              </div>
              <div className="text-xs text-muted-foreground/70">
                Erstellt am {new Date(project.createdAt).toLocaleDateString('de-DE')}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Cards */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {/* Staging Tool Card */}
        <button
          onClick={onOpenStaging}
          className="group p-4 bg-primary rounded-xl text-white text-left
                   hover:bg-primary/90 transition-all shadow-md hover:shadow-lg
                   hover:scale-[1.01] active:scale-[0.99] w-full"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm shrink-0">
              <Wand2 className="w-5 h-5" />
            </div>
            <h3 className="text-base font-bold flex-1 min-w-0">Staging Tool öffnen</h3>
            <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full backdrop-blur-sm shrink-0">
              AI Staging
            </span>
          </div>
        </button>

        {/* Overlay Tool Card */}
        <button
          onClick={onOpenOverlay}
          className="group p-4 bg-gradient-to-br from-slate-700 to-slate-800 rounded-xl text-white text-left
                   hover:from-slate-800 hover:to-slate-900 transition-all shadow-md hover:shadow-lg
                   hover:scale-[1.01] active:scale-[0.99] w-full"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/10 rounded-lg backdrop-blur-sm shrink-0">
              <Layers className="w-5 h-5" />
            </div>
            <h3 className="text-base font-bold flex-1 min-w-0">Overlay Tool öffnen</h3>
            <span className="text-xs bg-white/10 px-2 py-0.5 rounded-full backdrop-blur-sm shrink-0">
              {overlayImageCount} Bilder verfügbar
            </span>
          </div>
        </button>

        {/* Floorplan Tool Card */}
        {floorplanEnabled && (
          <button
            onClick={onOpenFloorplan}
            className="group p-4 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl text-white text-left
                     hover:from-teal-600 hover:to-teal-700 transition-all shadow-md hover:shadow-lg
                     hover:scale-[1.01] active:scale-[0.99] w-full"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm shrink-0">
                <LayoutGrid className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold flex-1 min-w-0">
                {floorplan2dUrl ? 'Grundriss bearbeiten' : 'Grundriss-Tool öffnen'}
              </h3>
              <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full backdrop-blur-sm shrink-0">
                AI Grundriss
              </span>
            </div>
          </button>
        )}
      </div>

      {/* Tenant Upload Section */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <button
            className="flex items-center justify-between w-full"
            onClick={() => setTenantSectionCollapsed(!tenantSectionCollapsed)}
          >
            <h2 className="text-lg font-bold text-foreground">Mieter-Link</h2>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">Public Link</Badge>
              <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform", tenantSectionCollapsed && "-rotate-90")} />
            </div>
          </button>

          {!tenantSectionCollapsed && (
            <>
              <div className="flex flex-col md:flex-row gap-2">
                <Input
                  readOnly
                  value={tenantUploadLink}
                  placeholder="Link wird geladen..."
                  className="flex-1"
                />
                <div className="flex gap-2 shrink-0">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleCopyTenantLink}
                    disabled={!tenantUploadLink}
                  >
                    {copyStatus === 'copied' ? 'Kopiert' : copyStatus === 'error' ? 'Fehler' : 'Kopieren'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRegenerateToken}
                    disabled={isUpdatingUploadSettings}
                  >
                    Neuer Token
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={isUploadActive}
                    onCheckedChange={handleToggleUploadActive}
                    disabled={isUpdatingUploadSettings}
                  />
                  <Label className="text-sm">Link aktiv</Label>
                </div>
                <p className="text-xs text-muted-foreground">
                  {tenantUploadCount} Mieter-Uploads
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Hero Titelbild Compact Row */}
      {(() => {
        const heroImage = images.find(img => img.heroUrl);
        if (!heroImage) return null;
        return (
          <Card>
            <CardContent className="p-3">
              <div className="flex items-center gap-4">
                <img
                  src={heroImage.heroUrl}
                  alt="Titelbild"
                  className="h-16 w-auto rounded-md shadow-sm cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => onImageClick(heroImage.heroUrl!)}
                />
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Crown className="w-4 h-4 text-amber-500 shrink-0" />
                  <span className="text-sm font-bold">Titelbild</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={async () => {
                      try {
                        const response = await fetch(heroImage.heroUrl!);
                        const blob = await response.blob();
                        saveAs(blob, `titelbild_${project.name}.png`);
                      } catch (error) {
                        console.error('[Download] Hero download failed:', error);
                      }
                    }}
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setHeroEditorImage(heroImage)}
                  >
                    Bearbeiten
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Floorplan Results Section */}
      {floorplanEnabled && floorplan2dUrl && (
        <Card>
          <CardContent className="p-4 space-y-3">
            {/* Header — clickable to collapse */}
            <div className="flex items-center justify-between">
              <button
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                onClick={() => setFloorplanCollapsed(!floorplanCollapsed)}
              >
                <LayoutGrid className="w-4 h-4 text-teal-500" />
                <span className="font-bold">Grundriss</span>
                <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform", floorplanCollapsed && "-rotate-90")} />
              </button>
              <Button variant="outline" size="sm" onClick={onOpenFloorplan}>
                <LayoutGrid className="w-3.5 h-3.5 mr-2" />
                Tool öffnen
              </Button>
            </div>

            {/* Previews (collapsible) */}
            {!floorplanCollapsed && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* 2D Preview — shows side-by-side "Mit Branding" + "Ohne Branding" when branded composite exists */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">2D Grundriss</Badge>
                </div>

                {floorplan2dBrandedUrl ? (
                  // Both variants exist — render side-by-side comparison
                  <div className="grid grid-cols-2 gap-2">
                    {/* Mit Branding */}
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-1">
                        <Badge className="bg-teal-600 hover:bg-teal-700 border-transparent text-white text-[10px] px-1.5 py-0">
                          Mit Branding
                        </Badge>
                      </div>
                      <div
                        className="rounded-lg overflow-hidden border bg-muted/30 cursor-pointer hover:ring-2 hover:ring-primary/40 transition-all"
                        onClick={() => floorplan2dBrandedUrl && onImageClick(floorplan2dBrandedUrl)}
                      >
                        <img src={floorplan2dBrandedUrl} alt="2D Grundriss mit Branding" className="w-full h-auto" />
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 gap-1 px-2"
                          onClick={() => floorplan2dBrandedUrl && onImageClick(floorplan2dBrandedUrl)}
                          title="Ansicht"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          <span className="text-xs">Ansicht</span>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 gap-1 px-2"
                          onClick={() => floorplan2dBrandedUrl && saveAs(floorplan2dBrandedUrl, `${project.name}_grundriss-mit-branding.png`)}
                          title="Herunterladen"
                        >
                          <Download className="w-3.5 h-3.5" />
                          <span className="text-xs">Download</span>
                        </Button>
                      </div>
                    </div>
                    {/* Ohne Branding */}
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-1">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          Ohne Branding
                        </Badge>
                      </div>
                      <div
                        className="rounded-lg overflow-hidden border bg-muted/30 cursor-pointer hover:ring-2 hover:ring-primary/40 transition-all"
                        onClick={() => cleanFloorplan2dUrl && onImageClick(cleanFloorplan2dUrl)}
                      >
                        {cleanFloorplan2dUrl && (
                          <img src={cleanFloorplan2dUrl} alt="2D Grundriss ohne Branding" className="w-full h-auto" />
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 gap-1 px-2"
                          disabled={!cleanFloorplan2dUrl}
                          onClick={() => cleanFloorplan2dUrl && onImageClick(cleanFloorplan2dUrl)}
                          title="Ansicht"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          <span className="text-xs">Ansicht</span>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 gap-1 px-2"
                          disabled={!cleanFloorplan2dUrl}
                          onClick={() => cleanFloorplan2dUrl && saveAs(cleanFloorplan2dUrl, `${project.name}_grundriss-ohne-branding.png`)}
                          title="Herunterladen"
                        >
                          <Download className="w-3.5 h-3.5" />
                          <span className="text-xs">Download</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Only the clean variant exists — single tile + "Branding hinzufügen" CTA
                  <>
                    <div
                      className="rounded-lg overflow-hidden border bg-muted/30 cursor-pointer hover:ring-2 hover:ring-primary/40 transition-all"
                      onClick={() => cleanFloorplan2dUrl && onImageClick(cleanFloorplan2dUrl)}
                    >
                      {cleanFloorplan2dUrl && (
                        <img src={cleanFloorplan2dUrl} alt="2D Grundriss" className="w-full h-auto" />
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 gap-2"
                        disabled={!cleanFloorplan2dUrl}
                        onClick={() => cleanFloorplan2dUrl && saveAs(cleanFloorplan2dUrl, `${project.name}_grundriss_2d.png`)}
                      >
                        <Download className="w-3.5 h-3.5" />
                        2D herunterladen
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 gap-2 bg-[#fe7f2e] hover:bg-[#e56a1f] text-white"
                        onClick={onOpenFloorplan}
                      >
                        Branding hinzufügen
                      </Button>
                    </div>
                  </>
                )}

                {/* Version history thumbnail strip */}
                {floorplanVersions.length > 1 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1.5">Versionen</p>
                    <div className="flex gap-1.5 overflow-x-auto pb-1">
                      {floorplanVersions.map((version) => {
                        const versionUrl = version.initialStoragePath ? getImageUrl(version.initialStoragePath) : null;
                        if (!versionUrl) return null;
                        const isActive = selectedVersionId === version.id;
                        return (
                          <button
                            key={version.id}
                            type="button"
                            onClick={() => setSelectedVersionId(version.id)}
                            className={cn(
                              'flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 bg-white transition-all',
                              isActive ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-primary/50'
                            )}
                            title={`Version ${version.versionNumber}`}
                          >
                            <img src={versionUrl} alt="" className="w-full h-full object-contain p-0.5" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* 3D Preview or Generate Button */}
              {displayedFloorplan3dUrl ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-emerald-600 hover:bg-emerald-700 border-transparent text-white gap-1">
                      <Box className="w-3 h-3" />
                      3D Dollhouse
                    </Badge>
                  </div>

                  {floorplan3dBrandedUrl ? (
                    // Both 3D variants exist — render side-by-side comparison
                    <div className="grid grid-cols-2 gap-2">
                      {/* Mit Branding */}
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-1">
                          <Badge className="bg-teal-600 hover:bg-teal-700 border-transparent text-white text-[10px] px-1.5 py-0">
                            Mit Branding
                          </Badge>
                        </div>
                        <div
                          className="relative rounded-lg overflow-hidden border bg-muted/30 cursor-pointer hover:ring-2 hover:ring-primary/40 transition-all"
                          onClick={() => !is3dGenerating && floorplan3dBrandedUrl && onImageClick(floorplan3dBrandedUrl)}
                        >
                          <img src={floorplan3dBrandedUrl} alt="3D Grundriss mit Branding" className={cn("w-full h-auto", is3dGenerating && "opacity-40")} />
                          {is3dGenerating && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                              <Loader2 className="w-6 h-6 text-primary animate-spin" />
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 gap-1 px-2"
                            onClick={() => floorplan3dBrandedUrl && onImageClick(floorplan3dBrandedUrl)}
                            title="Ansicht"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            <span className="text-xs">Ansicht</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 gap-1 px-2"
                            onClick={() => floorplan3dBrandedUrl && saveAs(floorplan3dBrandedUrl, `${project.name}_grundriss-3d-mit-branding.png`)}
                            title="Herunterladen"
                          >
                            <Download className="w-3.5 h-3.5" />
                            <span className="text-xs">Download</span>
                          </Button>
                        </div>
                      </div>
                      {/* Ohne Branding */}
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-1">
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                            Ohne Branding
                          </Badge>
                        </div>
                        <div
                          className="relative rounded-lg overflow-hidden border bg-muted/30 cursor-pointer hover:ring-2 hover:ring-primary/40 transition-all"
                          onClick={() => !is3dGenerating && cleanFloorplan3dUrl && onImageClick(cleanFloorplan3dUrl)}
                        >
                          {cleanFloorplan3dUrl && (
                            <img src={cleanFloorplan3dUrl} alt="3D Grundriss ohne Branding" className={cn("w-full h-auto", is3dGenerating && "opacity-40")} />
                          )}
                          {is3dGenerating && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                              <Loader2 className="w-6 h-6 text-primary animate-spin" />
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 gap-1 px-2"
                            disabled={!cleanFloorplan3dUrl}
                            onClick={() => cleanFloorplan3dUrl && onImageClick(cleanFloorplan3dUrl)}
                            title="Ansicht"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            <span className="text-xs">Ansicht</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 gap-1 px-2"
                            disabled={!cleanFloorplan3dUrl}
                            onClick={() => cleanFloorplan3dUrl && saveAs(cleanFloorplan3dUrl, `${project.name}_grundriss-3d-ohne-branding.png`)}
                            title="Herunterladen"
                          >
                            <Download className="w-3.5 h-3.5" />
                            <span className="text-xs">Download</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Only the clean 3D variant exists — single tile
                    <>
                      <div
                        className="relative rounded-lg overflow-hidden border bg-muted/30 cursor-pointer hover:ring-2 hover:ring-primary/40 transition-all"
                        onClick={() => !is3dGenerating && displayedFloorplan3dUrl && onImageClick(displayedFloorplan3dUrl)}
                      >
                        <img src={displayedFloorplan3dUrl} alt="3D Grundriss" className={cn("w-full h-auto", is3dGenerating && "opacity-40")} />
                        {is3dGenerating && (
                          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                            <Loader2 className="w-8 h-8 text-primary animate-spin" />
                            <p className="text-sm font-medium">Wird neu generiert...</p>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 gap-2"
                          onClick={() => displayedFloorplan3dUrl && saveAs(displayedFloorplan3dUrl, `${project.name}_grundriss_3d.png`)}
                        >
                          <Download className="w-3.5 h-3.5" />
                          3D herunterladen
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleGenerate3D}
                          disabled={is3dGenerating || !floorplan2dPath}
                        >
                          <RefreshCw className={cn("w-3.5 h-3.5", is3dGenerating && "animate-spin")} />
                        </Button>
                      </div>
                    </>
                  )}
                  {generate3dError && (
                    <p className="text-xs text-destructive">{generate3dError}</p>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center border rounded-lg bg-muted/30 p-6 gap-3 h-full">
                  {is3dGenerating ? (
                    <>
                      <div className="relative">
                        <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                          <Box className="w-7 h-7 text-primary" />
                        </div>
                        <Loader2 className="w-6 h-6 text-primary animate-spin absolute -top-1 -right-1" />
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium">3D wird generiert...</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Bis zu 60 Sekunden
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-14 h-14 rounded-xl bg-muted flex items-center justify-center">
                        <Box className="w-7 h-7 text-muted-foreground" />
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium">3D-Dollhouse-Ansicht</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Isometrische 3D-Darstellung erstellen
                        </p>
                      </div>
                      <Button
                        onClick={handleGenerate3D}
                        disabled={!floorplan2dPath}
                        className="gap-2"
                      >
                        <Box className="w-4 h-4" />
                        3D generieren (2 Credits)
                      </Button>
                    </>
                  )}
                  {generate3dError && (
                    <p className="text-xs text-destructive text-center">{generate3dError}</p>
                  )}
                </div>
              )}
            </div>}
          </CardContent>
        </Card>
      )}

      {/* Gallery Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-foreground">Bisherige Bilder</h2>
          <div className="flex items-center gap-3">
            {/* Selection Mode Controls */}
            {isSelectionMode ? (
              <>
                <span className="text-sm text-muted-foreground">
                  {selectedImages.size} ausgewählt
                </span>
                <Button
                  variant="ghost"
                  onClick={selectedImages.size === images.length ? deselectAllImages : selectAllImages}
                  className="text-primary hover:text-primary/80"
                >
                  {selectedImages.size === images.length ? 'Keine' : 'Alle'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setDownloadNotice(null); setShowDownloadModal(true); }}
                  disabled={selectedImages.size === 0}
                  className="gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteSelected}
                  disabled={selectedImages.size === 0 || isDeleting}
                  className="gap-2"
                >
                  {isDeleting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  Löschen
                </Button>
                <Button
                  variant="default"
                  onClick={() => onOpenStagingWithSelection(selectedImages)}
                  disabled={selectedImages.size === 0}
                  className="gap-2"
                >
                  <Wand2 className="w-4 h-4" />
                  Staging starten
                </Button>
                <Button
                  variant="secondary"
                  onClick={toggleSelectionMode}
                  className="gap-2"
                >
                  <X className="w-4 h-4" />
                  Abbrechen
                </Button>
              </>
            ) : (
              <>
                {images.length > 0 && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => { setDownloadNotice(null); setShowDownloadModal(true); }}
                      className="gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Alle herunterladen
                    </Button>
                    <Button
                      variant="outline"
                      onClick={toggleSelectionMode}
                      className="gap-2"
                    >
                      <CheckSquare className="w-4 h-4" />
                      Auswählen
                    </Button>
                  </>
                )}
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingFiles}
                  className="gap-2"
                >
                  {isUploadingFiles ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {uploadProgress ? `${uploadProgress.done}/${uploadProgress.total}` : '...'}
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Hochladen
                    </>
                  )}
                </Button>
                <span className="text-sm text-muted-foreground">{images.length} Räume</span>
              </>
            )}
          </div>
        </div>

        {isLoadingImages ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : images.length === 0 ? (
          <Card
            className="text-center py-16 border-dashed cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <CardContent>
              <Upload className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground">Bilder hochladen</p>
              <p className="text-sm text-muted-foreground/70 mt-1">
                Klicke hier oder starte das Staging Tool
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {images.map((image) => (
              <GalleryCard
                key={image.id}
                image={image}
                isSelectionMode={isSelectionMode}
                isSelected={selectedImages.has(image.id)}
                onImageClick={() => isSelectionMode ? toggleImageSelection(image.id) : setSelectedRoom(image)}
                onToggleSelection={() => toggleImageSelection(image.id)}
                onHeroEdit={() => setHeroEditorImage(image)}
                onStartStaging={() => onOpenStagingWithSelection(new Set([image.id]))}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Gallery Card Component
// ============================================================================

interface GalleryCardProps {
  image: GalleryImage;
  isSelectionMode: boolean;
  isSelected: boolean;
  onImageClick: () => void;
  onToggleSelection: () => void;
  onHeroEdit?: () => void;
  onStartStaging?: () => void;
}

const GalleryCard: React.FC<GalleryCardProps> = ({
  image,
  isSelectionMode,
  isSelected,
  onImageClick,
  onToggleSelection,
  onHeroEdit,
  onStartStaging,
}) => {
  // Show branded > branded_empty > upscaled_staged > upscaled_empty > staged > empty > original (best quality first)
  const displayUrl = image.brandedUrl || image.brandedEmptyUrl || image.upscaledStagedUrl || image.upscaledEmptyUrl || image.stagedUrl || image.emptyUrl || image.originalUrl;
  // Use custom label if available, otherwise get German room label
  const label = image.customLabel || getRoomLabel(image.roomType);
  
  // Determine type badge (show best available) - German labels
  const getTypeBadge = () => {
    if (image.brandedUrl) return { text: 'Gebrandet', className: 'bg-purple-500 hover:bg-purple-600 border-transparent text-white' };
    if (image.brandedEmptyUrl) return { text: 'Gebr. Leer', className: 'bg-cyan-500 hover:bg-cyan-600 border-transparent text-white' };
    if (image.upscaledStagedUrl) return { text: 'Hochskaliert', className: 'bg-orange-500 hover:bg-orange-600 border-transparent text-white' };
    if (image.upscaledEmptyUrl) return { text: 'Hochsk. Leer', className: 'bg-teal-500 hover:bg-teal-600 border-transparent text-white' };
    if (image.stagedUrl) return { text: 'Eingerichtet', className: 'bg-green-500 hover:bg-green-600 border-transparent text-white' };
    if (image.emptyUrl) return { text: 'Leer', className: 'bg-blue-500 hover:bg-blue-600 border-transparent text-white' };
    return { text: 'Original', variant: 'secondary' as const };
  };
  
  const badgeProps = getTypeBadge();
  const isTenantUpload = image.isTenantUpload;

  // Count available versions (including brandedEmptyUrl)
  const versionCount = [image.brandedUrl, image.brandedEmptyUrl, image.upscaledStagedUrl, image.upscaledEmptyUrl, image.stagedUrl, image.emptyUrl, image.originalUrl].filter(Boolean).length;

  if (!displayUrl) return null;

  return (
    <Card 
      className={cn(
        "group relative overflow-hidden cursor-pointer transition-all duration-200 border-border",
        isSelected ? "ring-2 ring-primary border-primary" : "hover:shadow-md"
      )}
      onClick={onImageClick}
    >
      {/* Selection Checkbox */}
      {isSelectionMode && (
        <div 
          className="absolute top-2 left-2 z-20"
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelection();
          }}
        >
          {isSelected ? (
            <div className="bg-primary text-primary-foreground rounded-sm">
              <CheckSquare className="w-6 h-6" />
            </div>
          ) : (
            <div className="bg-background/80 text-muted-foreground rounded-sm hover:text-primary backdrop-blur-sm">
              <Square className="w-6 h-6" />
            </div>
          )}
        </div>
      )}

      {/* Image */}
      <div className="aspect-[4/3] bg-muted relative overflow-hidden">
        <img
          src={displayUrl}
          alt={label}
          className={cn(
            "w-full h-full object-cover transition-transform duration-300",
            isSelectionMode ? "" : "group-hover:scale-105"
          )}
        />
        {!isSelectionMode && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
            <Maximize2 className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
          </div>
        )}
        {/* Staging Button (hover) — only for unstaged images */}
        {!isSelectionMode && onStartStaging && !image.stagedUrl && !image.emptyUrl && (
          <button
            onClick={(e) => { e.stopPropagation(); onStartStaging(); }}
            className="absolute bottom-2 left-2 z-10 bg-primary/90 hover:bg-primary text-primary-foreground text-xs px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 backdrop-blur-sm"
            title="Staging starten"
          >
            <Wand2 className="w-3 h-3" />
            Staging starten
          </button>
        )}
        {/* Hero Edit Button (hover) */}
        {!isSelectionMode && onHeroEdit && displayUrl && (
          <button
            onClick={(e) => { e.stopPropagation(); onHeroEdit(); }}
            className="absolute bottom-2 right-2 z-10 bg-black/70 hover:bg-black/90 text-white text-xs px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 backdrop-blur-sm"
            title="Als Titelbild bearbeiten"
          >
            <Crown className="w-3 h-3" />
            Titelbild
          </button>
        )}
        
        {/* Selected Overlay */}
        {isSelected && (
          <div className="absolute inset-0 bg-primary/20" />
        )}
      </div>

      {/* Badge */}
      <div className="absolute top-2 right-2">
        <Badge 
          variant={badgeProps.variant || 'default'} 
          className={badgeProps.className}
        >
          {badgeProps.text}
        </Badge>
      </div>

      {/* Tenant badge, hero badge, and version count */}
      {!isSelectionMode && (
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {image.heroOverlayConfig && (
            <Badge className="bg-amber-500 hover:bg-amber-600 border-transparent text-white gap-1">
              <Crown className="w-3 h-3" /> Titelbild
            </Badge>
          )}
          {isTenantUpload && (
            <Badge className="bg-amber-500 hover:bg-amber-600 border-transparent text-white">
              Vom Mieter
            </Badge>
          )}
          {versionCount > 1 && (
            <Badge variant="secondary" className="bg-black/50 text-white hover:bg-black/60 backdrop-blur-sm border-none">
              {versionCount} Versionen
            </Badge>
          )}
        </div>
      )}

      {/* Info */}
      <CardContent className="p-3">
        <p className="font-medium text-foreground text-sm truncate">{label}</p>
        <p className="text-xs text-muted-foreground">
          {new Date(image.createdAt).toLocaleDateString('de-DE')}
        </p>
      </CardContent>
    </Card>
  );
};
